import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Row,
  Col,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import WarehouseItemTable from "../warehouse/WarehouseItemTable";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { Popover } from "react-tiny-popover";
import Select from "react-select";
import { EditIcon, TrashIcon } from "../Icons";

const AssetCertification = ({ show = true, onHide = () => {} }) => {
  const { backendUrl, token } = useAuth();

  const [formData, setFormData] = useState({
    Bar_Code: "",
    certificationId: "",
  });
  const [showItemsPopover, setShowItemsPopover] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [actionError, setActionError] = useState("");
  const queryClient = useQueryClient();

  const normalizeArrayPayload = payload => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (payload && typeof payload === "object") {
      if (Array.isArray(payload.data)) {
        return payload.data;
      }

      if (payload.data && Array.isArray(payload.data.assetRequirements)) {
        return payload.data.assetRequirements;
      }

      if (payload.data && Array.isArray(payload.data.rows)) {
        return payload.data.rows;
      }

      if (Array.isArray(payload.assetRequirements)) {
        return payload.assetRequirements;
      }

      if (Array.isArray(payload.rows)) {
        return payload.rows;
      }
    }

    return [];
  };

  const normalizeCertificationRows = payload => {
    return normalizeArrayPayload(payload);
  };

  const fetchCertificationTypes = async () => {
    const response = await fetch(
      `${backendUrl}/api/partnership/certification-types`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch certification types.");
    }

    const payload = await response.json();
    const rows = normalizeArrayPayload(payload);

    return rows.map(item => ({
      value:
        item.certificationId || item.id || item.value || item.code || item.name,
      label:
        item.certificationName ||
        item.name ||
        item.label ||
        item.certificationId ||
        item.id,
    }));
  };

  const fetchCertifications = async () => {
    const response = await fetch(`${backendUrl}/api/asset-requirements`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch certifications.");
    }

    const payload = await response.json();
    return normalizeCertificationRows(payload);
  };

  const { data: certifications = [], isLoading, isFetching, error } = useQuery(
    ["asset-certifications"],
    fetchCertifications,
    {
      enabled: show,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: certificationTypeOptions = [],
    isLoading: isLoadingCertificationTypes,
    error: certificationTypesError,
  } = useQuery(["certification-types"], fetchCertificationTypes, {
    enabled: show,
    refetchOnWindowFocus: false,
  });

  const createMutation = useMutation(
    async payload => {
      const response = await fetch(`${backendUrl}/api/asset-requirements`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create certification.");
      }

      const payloadResponse = await response.json();
      return normalizeCertificationRows(payloadResponse);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["asset-certifications"]);
        resetForm();
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not create certification."
        );
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, payload }) => {
      const response = await fetch(
        `${backendUrl}/api/asset-requirements/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update certification.");
      }

      const payloadResponse = await response.json();
      return normalizeCertificationRows(payloadResponse);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["asset-certifications"]);
        resetForm();
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not update certification."
        );
      },
    }
  );

  const deleteMutation = useMutation(
    async id => {
      const response = await fetch(
        `${backendUrl}/api/asset-requirements/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete certification.");
      }

      return id;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["asset-certifications"]);
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not delete certification."
        );
      },
    }
  );

  const handleCreate = async e => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleUpdate = async id => {
    updateMutation.mutate({ id, payload: formData });
  };

  const handleDelete = async id => {
    deleteMutation.mutate(id);
  };

  const handleEdit = cert => {
    setEditingId(cert.id);
    setFormData({
      Bar_Code: cert.Bar_Code,
      certificationId: cert.certificationId,
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ Bar_Code: "", certificationId: "" });
    setActionError("");
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;
  const isDeleting = deleteMutation.isLoading;
  const selectedCertificationOption =
    certificationTypeOptions.find(
      option => String(option.value) === String(formData.certificationId)
    ) || null;
  const getCertificationLabel = certificationId => {
    const selectedOption = certificationTypeOptions.find(
      option => String(option.value) === String(certificationId)
    );

    return selectedOption?.label || certificationId;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Asset Requirements</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form
          onSubmit={
            editingId
              ? e => {
                  e.preventDefault();
                  handleUpdate(editingId);
                }
              : handleCreate
          }
          className="mb-4"
        >
          {!!actionError && (
            <Alert variant="danger" className="mb-3">
              {actionError}
            </Alert>
          )}

          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Group controlId="assetCertificationBarCode">
                <Form.Label>Item Code</Form.Label>

                <InputGroup>
                  <Form.Control
                    name="barcode"
                    placeholder="Enter Item Code"
                    value={formData.Bar_Code}
                    onChange={e => {}}
                    readOnly
                  />

                  <Popover
                    isOpen={showItemsPopover}
                    onClickOutside={() => setShowItemsPopover(false)}
                    content={() => (
                      <WarehouseItemTable
                        handleSelectedItem={item => {
                          setFormData({ ...formData, Bar_Code: item.Bar_Code });
                          setShowItemsPopover(false);
                        }}
                      />
                    )}
                    position="bottom"
                  >
                    <InputGroup.Text
                      onClick={() => setShowItemsPopover(!showItemsPopover)}
                    >
                      <MagnifyIcon />
                    </InputGroup.Text>
                  </Popover>
                </InputGroup>
              </Form.Group>
            </Col>

            <Col md={5}>
              <Form.Group controlId="assetCertificationId">
                <Form.Label>Certification ID</Form.Label>
                <Select
                  options={certificationTypeOptions}
                  value={selectedCertificationOption}
                  onChange={option =>
                    setFormData({
                      ...formData,
                      certificationId: option?.value || "",
                    })
                  }
                  placeholder="Select certification"
                  isClearable
                  isLoading={isLoadingCertificationTypes}
                  isDisabled={isSubmitting || isLoadingCertificationTypes}
                />
              </Form.Group>
            </Col>

            <Col md={2} className="d-grid">
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : editingId ? (
                  "Update"
                ) : (
                  "Add"
                )}
              </Button>
            </Col>
          </Row>

          {!!certificationTypesError && (
            <div className="text-danger mt-1 small">
              {certificationTypesError?.message ||
                "Could not load certification types."}
            </div>
          )}

          {editingId && (
            <div className="mt-3">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={resetForm}
                disabled={isSubmitting}
              >
                Cancel Edit
              </Button>
            </div>
          )}
        </Form>

        {isLoading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" />
            <span>Loading certifications...</span>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            {error?.message || "Error loading certifications."}
          </Alert>
        ) : (
          <>
            {isFetching && (
              <div className="d-flex align-items-center gap-2 mb-3">
                <Spinner animation="border" size="sm" />
                <span>Refreshing data...</span>
              </div>
            )}

            <Table
              striped
              borderless
              hover
              responsive
              className="product-table mb-0"
            >
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Item Code</th>
                  <th>Certification</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {certifications.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No certifications found.
                    </td>
                  </tr>
                )}

                {certifications.map(cert => (
                  <tr key={cert.id}>
                    <td>{cert.id}</td>
                    <td>{cert.Bar_Code}</td>
                    <td>{getCertificationLabel(cert.certificationId)}</td>
                    <td>{new Date(cert.created_at).toLocaleDateString()}</td>
                    <td className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant=""
                        onClick={() => handleEdit(cert)}
                        disabled={isSubmitting || isDeleting}
                      >
                        <EditIcon />
                      </Button>{" "}
                      &nbsp; &nbsp;
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(cert.id)}
                        disabled={isSubmitting || isDeleting}
                      >
                        {isDeleting ? "Deleting..." : "Delete"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssetCertification;
